import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html, type PropertyValues } from 'lit'
import { property, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { catchError, map, of, ReplaySubject, startWith, switchMap, takeUntil } from 'rxjs'
import type { FileViewerItem } from './file-viewer'
import { xmlDocument, XmlPreviewError, type XmlDocument } from './xml'
import '../button/button'
import '../copy/copy'
import '../layout/scroll/scroll'
import '../tabs/index'
import '../typography/typography'

type XmlResult =
	{ status: 'empty' | 'loading' } | { status: 'ready'; document: XmlDocument } | { status: 'error'; code: string }

@schmancyElement('schmancy-xml-stage')
export class SchmancyXmlStage extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				height: 100%;
				min-height: 0;
				min-width: 0;
				container-type: inline-size;
			}
		`,
	]
	@property({ attribute: false }) file?: FileViewerItem
	@state() private result: XmlResult = { status: 'empty' }
	@state() private linePage = 0
	private files = new ReplaySubject<FileViewerItem | undefined>(1)

	override connectedCallback() {
		super.connectedCallback()
		this.files
			.pipe(
				switchMap(file =>
					file
						? xmlDocument(file.previewUrl).pipe(
								map((document): XmlResult => ({ status: 'ready', document })),
								catchError(error =>
									of<XmlResult>({ status: 'error', code: error instanceof XmlPreviewError ? error.code : 'request' }),
								),
								startWith<XmlResult>({ status: 'loading' }),
							)
						: of<XmlResult>({ status: 'empty' }),
				),
				takeUntil(this.disconnecting),
			)
			.subscribe(result => {
				this.result = result
			})
	}

	override willUpdate(changed: PropertyValues<this>) {
		if (changed.has('file')) {
			this.linePage = 0
			this.files.next(this.file)
		}
	}

	override render() {
		if (this.result.status === 'empty') return html``
		const failureCode = this.result.status === 'error' ? this.result.code : undefined
		if (this.result.status !== 'ready')
			return html`<div
				class="grid content-center justify-items-start gap-4 h-full p-6"
				role=${this.result.status === 'error' ? 'alert' : 'status'}
				aria-live="polite"
			>
				<schmancy-typography type="title" token="md"
					>${componentText(this.result.status === 'loading' ? 'xml.loading' : 'xml.failed')}</schmancy-typography
				>
				${when(
					this.result.status === 'error',
					() => html`
						<schmancy-typography type="body" token="md" class="text-surface-onVariant"
							>${componentText(
								failureCode === 'unsafe'
									? 'xml.unsafe'
									: failureCode === 'malformed'
										? 'xml.malformed'
										: failureCode === 'too-large'
											? 'xml.tooLarge'
											: failureCode === 'unreadable'
												? 'xml.unreadable'
												: 'xml.requestFailed',
							)}</schmancy-typography
						>
						${when(
							failureCode === 'request',
							() =>
								html`<schmancy-button
									variant="filled tonal"
									@click=${() => {
										if (this.file) this.files.next(this.file)
									}}
									>${componentText('xml.retry')}</schmancy-button
								>`,
						)}
					`,
				)}
			</div>`
		const document = this.result.document
		const source = html`<schmancy-scroll class="block h-full min-h-0" tabindex="0" label=${componentText('xml.source')}>
			<div class="p-4 @xl:px-6">
				<div class="flex justify-between items-center gap-4 mb-4">
					<schmancy-typography type="label" token="md" class="text-surface-onVariant"
						>${componentText('xml.sourceDescription')}</schmancy-typography
					>
					<schmancy-copy .value=${document.source} subject=${componentText('xml.source')}></schmancy-copy>
				</div>
				<pre class="m-0 font-mono text-sm leading-relaxed whitespace-pre-wrap wrap-anywhere">${document.source}</pre>
			</div>
		</schmancy-scroll>`
		if (document.kind === 'xml') return html`${source}`
		const invoice = document.invoice
		return html`<schmancy-tab-group
			class="min-h-0"
			keep-mounted
			label=${componentText('xml.view')}
		>
			<schmancy-tab class="block h-full min-h-0 min-w-0" value="invoice" label=${componentText('xml.invoiceView')}>
				<schmancy-scroll
					class="block h-full min-h-0"
					tabindex="0"
					label=${this.file?.name || componentText('xml.invoiceView')}
				>
					<article class="grid gap-6 p-4 @xl:gap-8 @xl:p-6 text-surface-on">
						<header class="grid grid-cols-1 @xl:grid-cols-[minmax(0,1fr)_auto] gap-6 items-start">
							<div class="grid content-start gap-2 min-w-0">
								<schmancy-typography type="label" token="md" class="text-surface-onVariant"
									>${invoice.syntax} XML ·
									${componentText(invoice.typeCode === '381' ? 'xml.creditNote' : invoice.typeCode === '380' ? 'xml.invoice' : 'xml.document')}${when(invoice.typeCode && !['380', '381'].includes(invoice.typeCode), () => ` · ${componentText('xml.documentType')}: ${invoice.typeCode}`)}</schmancy-typography
								>
								<schmancy-typography
									type="headline"
									token="md"
									class="wrap-anywhere whitespace-pre-wrap"
									role="heading"
									aria-level="2"
									>${invoice.number || componentText('xml.numberMissing')}</schmancy-typography
								>
								<div class="flex flex-wrap gap-x-6 gap-y-2">
									${when(invoice.issueDate, () => html`<schmancy-typography type="body" token="sm">${componentText('xml.issued')}: ${invoice.issueDate}</schmancy-typography>`)}
									${when(invoice.dueDate, () => html`<schmancy-typography type="body" token="sm">${componentText('xml.dueDate')}: ${invoice.dueDate}</schmancy-typography>`)}
								</div>
							</div>
							${when(
								invoice.totals.due,
								() =>
									html`<div class="grid content-start gap-2 min-w-0 text-start @xl:text-end tabular-nums">
										<schmancy-typography type="label" token="md" class="text-surface-onVariant"
											>${componentText('xml.amountDue')}</schmancy-typography
										>
										<schmancy-typography type="headline" token="lg"
											>${invoice.totals.due} ${invoice.currency || ''}</schmancy-typography
										>
									</div>`,
							)}
						</header>
						<div class="grid grid-cols-1 @xl:grid-cols-2 gap-6 @xl:gap-8">
							${repeat(
								[
									{ label: componentText('xml.seller'), party: invoice.seller },
									{ label: componentText('xml.buyer'), party: invoice.buyer },
								],
								(_item, index) => index,
								({ label, party }) =>
									html`<section class="grid content-start gap-2 min-w-0">
										<schmancy-typography
											type="label"
											token="md"
											class="text-surface-onVariant"
											role="heading"
											aria-level="3"
											>${label}</schmancy-typography
										>
										<schmancy-typography type="title" token="md" class="wrap-anywhere whitespace-pre-wrap"
											>${party.name || componentText('xml.nameMissing')}</schmancy-typography
										>
										${repeat(
											party.address,
											(_item, index) => index,
											line =>
												html`<schmancy-typography type="body" token="sm" class="wrap-anywhere whitespace-pre-wrap"
													>${line}</schmancy-typography
												>`,
										)}
										${when(party.vatId, () => html`<schmancy-typography type="body" token="sm" class="wrap-anywhere whitespace-pre-wrap text-surface-onVariant">${componentText('xml.vatId')}: ${party.vatId}</schmancy-typography>`)}
										${when(party.taxId, () => html`<schmancy-typography type="body" token="sm" class="wrap-anywhere whitespace-pre-wrap text-surface-onVariant">${componentText('xml.taxId')}: ${party.taxId}</schmancy-typography>`)}
									</section>`,
							)}
						</div>
						${when(
							invoice.buyerReference || invoice.orderReference,
							() =>
								html`<div class="flex flex-wrap gap-x-6 gap-y-2">
									${when(invoice.buyerReference, () => html`<schmancy-typography type="body" token="sm" class="wrap-anywhere whitespace-pre-wrap">${componentText('xml.buyerReference')}: ${invoice.buyerReference}</schmancy-typography>`)}
									${when(invoice.orderReference, () => html`<schmancy-typography type="body" token="sm" class="wrap-anywhere whitespace-pre-wrap">${componentText('xml.orderReference')}: ${invoice.orderReference}</schmancy-typography>`)}
								</div>`,
						)}
						<section class="grid content-start gap-2 min-w-0">
							<schmancy-typography type="title" token="md" role="heading" aria-level="3"
								>${componentText('xml.items')}</schmancy-typography
							>
							${
								invoice.lines.length
									? html`<schmancy-scroll
											class="block max-w-full"
											direction="horizontal"
											tabindex="0"
											label=${componentText('xml.items')}
											@keydown=${(event: KeyboardEvent) => {
												if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') event.stopPropagation()
											}}
										>
											<table
												class="w-full border-collapse text-start tabular-nums"
												aria-label=${componentText('xml.items')}
											>
												<thead>
													<tr>
														${repeat(
															[
																'xml.description',
																'xml.quantity',
																'xml.unitPrice',
																'xml.taxRate',
																'xml.lineNet',
															] as const,
															(_item, index) => index,
															(key, index) =>
																html`<th
																	scope="col"
																	class=${index ? 'p-3 align-top bg-surface-container text-surface-onVariant text-end whitespace-nowrap' : 'p-3 align-top bg-surface-container text-surface-onVariant text-start min-w-48'}
																>
																	<schmancy-typography type="label" token="sm"
																		>${componentText(key)}</schmancy-typography
																	>
																</th>`,
														)}
													</tr>
												</thead>
												<tbody>
													${repeat(
														invoice.lines.slice(this.linePage * 50, (this.linePage + 1) * 50),
														(_item, index) => index,
														line =>
															html`<tr class="even:bg-surface-containerLow">
																<td class="p-3 align-top min-w-48">
																	<div class="grid content-start gap-2 min-w-0">
																		<schmancy-typography
																			type="body"
																			token="md"
																			class="wrap-anywhere whitespace-pre-wrap"
																			>${line.name || line.description || line.id || '—'}</schmancy-typography
																		>
																		${when(line.name && line.description, () => html`<schmancy-typography type="body" token="sm" class="wrap-anywhere whitespace-pre-wrap text-surface-onVariant">${line.description}</schmancy-typography>`)}
																	</div>
																</td>
																<td class="p-3 align-top text-end whitespace-nowrap">
																	<schmancy-typography type="body" token="sm"
																		>${line.quantity || '—'} ${line.unit || ''}</schmancy-typography
																	>
																</td>
																<td class="p-3 align-top text-end whitespace-nowrap">
																	<schmancy-typography type="body" token="sm"
																		>${line.unitPrice || '—'}${when(line.priceBasis, () => ` / ${line.priceBasis} ${line.priceBasisUnit || ''}`)}</schmancy-typography
																	>
																</td>
																<td class="p-3 align-top text-end whitespace-nowrap">
																	<schmancy-typography type="body" token="sm"
																		>${line.taxRate ? `${line.taxRate}%` : '—'}</schmancy-typography
																	>
																</td>
																<td class="p-3 align-top text-end whitespace-nowrap">
																	<schmancy-typography type="body" token="md">${line.net || '—'}</schmancy-typography>
																</td>
															</tr>`,
													)}
												</tbody>
											</table>
										</schmancy-scroll>`
									: html`<schmancy-typography type="body" token="sm" class="text-surface-onVariant"
											>${componentText('xml.noItems')}</schmancy-typography
										>`
							}
							${when(
								invoice.lines.length > 50,
								() =>
									html`<div class="flex justify-between items-center gap-4 mb-4">
										<schmancy-button
											size="sm"
											?disabled=${this.linePage === 0}
											@click=${() => {
												this.linePage = Math.max(0, this.linePage - 1)
											}}
											>${componentText('xml.previousItems')}</schmancy-button
										>
										<schmancy-typography type="label" token="sm" role="status" aria-live="polite"
											>${componentText('xml.itemRange', { start: this.linePage * 50 + 1, end: Math.min((this.linePage + 1) * 50, invoice.lines.length), total: invoice.lines.length })}</schmancy-typography
										>
										<schmancy-button
											size="sm"
											?disabled=${(this.linePage + 1) * 50 >= invoice.lines.length}
											@click=${() => {
												this.linePage = Math.min(Math.ceil(invoice.lines.length / 50) - 1, this.linePage + 1)
											}}
											>${componentText('xml.nextItems')}</schmancy-button
										>
									</div>`,
							)}
						</section>
						<div class="grid grid-cols-1 @xl:grid-cols-2 gap-6 @xl:gap-8">
							${when(
								Object.values(invoice.payment).some(Boolean),
								() =>
									html`<section class="grid content-start gap-2 min-w-0">
										<schmancy-typography type="title" token="md" role="heading" aria-level="3"
											>${componentText('xml.payment')}</schmancy-typography
										>
										${repeat(
											Object.entries(invoice.payment).filter(([, value]) => Boolean(value)),
											(_item, index) => index,
											([key, value]) =>
												html`<schmancy-typography type="body" token="sm" class="wrap-anywhere whitespace-pre-wrap">
													${componentText(key === 'means' ? 'xml.paymentMeans' : key === 'reference' ? 'xml.paymentReference' : key === 'terms' ? 'xml.paymentTerms' : key === 'iban' ? 'xml.iban' : key === 'accountId' ? 'xml.accountId' : key === 'bic' ? 'xml.bic' : key === 'bankIdentifier' ? 'xml.bankIdentifier' : 'xml.accountName')}:
													${value}
												</schmancy-typography>`,
										)}
									</section>`,
							)}
							<section class="grid gap-3 col-start-[-2] col-end-[-1]" aria-label=${componentText('xml.totals')}>
								${repeat(
									[
										['allowance', componentText('xml.allowance'), invoice.totals.allowance],
										['charge', componentText('xml.charge'), invoice.totals.charge],
										['net', componentText('xml.net'), invoice.totals.net],
										['tax', componentText('xml.tax'), invoice.totals.tax],
										['gross', componentText('xml.gross'), invoice.totals.gross],
										['prepaid', componentText('xml.prepaid'), invoice.totals.prepaid],
										['due', componentText('xml.amountDue'), invoice.totals.due],
									].filter(([, , value]) => value !== undefined),
									(_item, index) => index,
									([key, label, value]) =>
										html`<div
											class=${this.classMap({ 'flex justify-between gap-8 tabular-nums': true, 'bg-surface-container rounded-xl p-4': key === 'due' })}
										>
											<schmancy-typography type=${key === 'due' ? 'title' : 'body'} token="md"
												>${label}</schmancy-typography
											>
											<schmancy-typography type=${key === 'due' ? 'title' : 'body'} token="md"
												>${value} ${invoice.currency || ''}</schmancy-typography
											>
										</div>`,
								)}
							</section>
						</div>
						${when(
							invoice.taxes.length,
							() =>
								html`<section class="grid content-start gap-2 min-w-0">
									<schmancy-typography type="title" token="md" role="heading" aria-level="3"
										>${componentText('xml.taxBreakdown')}</schmancy-typography
									>
									${repeat(
										invoice.taxes,
										(_item, index) => index,
										tax =>
											html`<schmancy-typography type="body" token="sm" class="wrap-anywhere whitespace-pre-wrap"
												>${tax.category || ''}${when(tax.rate, () => ` · ${tax.rate}%`)}${when(tax.basis, () => ` · ${componentText('xml.taxBasis')}: ${tax.basis}`)}${when(tax.amount, () => ` · ${componentText('xml.tax')}: ${tax.amount} ${invoice.currency || ''}`)}${when(tax.exemption, () => ` · ${tax.exemption}`)}</schmancy-typography
											>`,
									)}
								</section>`,
						)}
						${when(
							invoice.notes.length,
							() =>
								html`<section class="grid content-start gap-2 min-w-0">
									<schmancy-typography type="title" token="md" role="heading" aria-level="3"
										>${componentText('xml.notes')}</schmancy-typography
									>${repeat(
										invoice.notes,
										(_item, index) => index,
										note =>
											html`<schmancy-typography type="body" token="sm" class="wrap-anywhere whitespace-pre-wrap"
												>${note}</schmancy-typography
											>`,
									)}
								</section>`,
						)}
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant"
							>${componentText('xml.readingNotice')}</schmancy-typography
						>
					</article>
				</schmancy-scroll>
			</schmancy-tab>
			<schmancy-tab class="block h-full min-h-0 min-w-0" value="source" label=${componentText('xml.source')}
				>${source}</schmancy-tab
			>
		</schmancy-tab-group>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-xml-stage': SchmancyXmlStage
	}
}
